import axios, { AxiosResponse } from 'axios';
import { setupInterceptorsTo } from '@/shared/services/httpInterceptors';
import { LagerLogoutRequest, RefreshLoginRequest } from '@/modules/ewerp/models/MitarbeiterModels';

import { Email } from '@/modules/ewerp/models/CommonModels';
import { WritePlanungKommentarRequest } from '@/modules/ewerp/models/PlanungModels';
import { CreateApiKeyRequest } from '../models/ApiKeyModels';
import { GetBestellungenPageRequest } from '../models/BestellungModels';

/* DEV */
// import config from '@/../appsettings.dev.json';

/* PROD */
const appsettings = await fetch('/appsettings.json');
const config = await appsettings.json();

const env = window.__ENV__ ?? null;

const BASE_URL = (env != undefined && env?.BASE_URL != undefined && env?.BASE_URL != '' ? env?.BASE_URL : config.api.baseUrl) + '/v1';
const TENANT = env != undefined && env?.AUTH_DEFAULT_REALM != undefined && env?.AUTH_DEFAULT_REALM != '' ? env?.AUTH_DEFAULT_REALM : config.auth.defaultRealm;

const apiClient = setupInterceptorsTo(
  axios.create({
    baseURL: BASE_URL,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      tenant: TENANT,
    },
  })
);

export default {
  //#region LAGER SERVICE

  // retryRequest(config: AxiosRequestConfig) {
  //   return apiClient.request(config);
  // },

  //#region MITARBEITER
  loginLager(): Promise<AxiosResponse> {
    return apiClient.get('/lager/mitarbeiter/login');
  },
  logoutLager(data: LagerLogoutRequest): Promise<AxiosResponse> {
    return apiClient.post('/lager/mitarbeiter/logout', data);
  },
  refreshLoginLager(data: RefreshLoginRequest): Promise<AxiosResponse> {
    return apiClient.post('/lager/mitarbeiter/refresh', data);
  },
  getMitarbeiter() {
    return apiClient.get('/lager/mitarbeiter/list');
  },
  //#endregion

  //#region  ApiKeys
  getAllApiKeys(): Promise<AxiosResponse> {
    return apiClient.get('/apikeys');
  },

  createApiKey(data: CreateApiKeyRequest): Promise<AxiosResponse> {
    return apiClient.post('/apikey', data);
  },

  deleteApiKey(id: number): Promise<AxiosResponse> {
    return apiClient.delete(`/apikey/${id}`);
  },
  //#endregion

  //#region Database
  isDatabaseUpToDate(): Promise<AxiosResponse> {
    return apiClient.get('/database/status');
  },

  updateDatabase(): Promise<AxiosResponse> {
    return apiClient.get('/database/update');
  },
  //#endregion

  //#region BESTELLUNG
  getBestellungenPage(data: GetBestellungenPageRequest): Promise<AxiosResponse> {
    return apiClient.get('/bestellungen/page', {
      params: {
        pageIndex: data.data.page,
        pageSize: data.data.pageSize,
        sortBy: data.data.sortBy,
        sortDirection: data.data.sortDirection,
        filter: data.data.filter,
        vonlagernr: data.vonlagernr,
        kommissionart: data.kommissionart,
        von: data.von,
        bis: data.bis,
      },
    });
  },

  getBestellungDetails(id: number): Promise<AxiosResponse> {
    return apiClient.get(`/bestellung/${id}/details`);
  },
  //#endregion

  //#region RECHNUNG
  upsertRechnung(id: number): Promise<AxiosResponse> {
    return apiClient.post(`/rechnung/bestellung/${id}`);
  },

  createEdiRechung(id: number): Promise<AxiosResponse> {
    return apiClient.put(`/rechnung/${id}/upsert/edi/invoice`);
  },
  //#endregion

  //#region HERSTELLUNG
  getBestandskontrolle(): Promise<AxiosResponse> {
    return apiClient.get('/lager/herstellung/bestandskontrolle');
  },

  getBestandskontrolleRohstoffGruppiertTage(tage: number): Promise<AxiosResponse> {
    return apiClient.get(`/lager/herstellung/bestandskontrolle/${tage}`);
  },
  //#endregion

  //#region PRODUKTION
  getLagerMaschinen(): Promise<AxiosResponse> {
    return apiClient.get('/lager/produktion/maschinen');
  },

  getMaschinenPlanungHeute(maschinenname: string): Promise<AxiosResponse> {
    return apiClient.get(`/lager/produktion/planung/${maschinenname}`);
  },

  // datum im Format YYYY-MM-DD
  getProduktionsauftraegeDatum(datum: string): Promise<AxiosResponse> {
    return apiClient.get(`/lager/produktionsauftraege/datum/${datum}`);
  },

  getMaschinenPlanungHeuteListeartikel(maschinenname: string): Promise<AxiosResponse> {
    return apiClient.get(`/lager/produktion/planung/listeartikel/${maschinenname}`);
  },

  writePlanungKommentar(data: WritePlanungKommentarRequest): Promise<AxiosResponse> {
    return apiClient.put(`/lager/produktion/planung/kommentar`, data);
  },
  //#endregion PRODUKTION

  //#region LAGER LAND
  getAllLaender(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/land/alle`);
  },
  //#endregion

  //#region Energiezaehler
  getMaschineLeistungWerte(maschinenr: number): Promise<AxiosResponse> {
    return apiClient.get(`/energiezaehler/werte/lasthour/maschinenr/` + maschinenr);
  },
  //#endregion

  //#region LAGER
  getLagerArtikelBestand(): Promise<AxiosResponse> {
    return apiClient.get('/lager/artikel/bestand/dashboard');
  },
  getLagerplaetze(): Promise<AxiosResponse> {
    return apiClient.get('/lager/lagerplaetze');
  },
  getLagerplatzarten(): Promise<AxiosResponse> {
    return apiClient.get('/lager/lagerplatzarten');
  },
  getLagerpuffer(): Promise<AxiosResponse> {
    return apiClient.get('/lager/lagerpuffer');
  },
  getEinlagernEinzelnPPS(id: number): Promise<AxiosResponse> {
    return apiClient.get(`/lager/einlagern/einzeln/pps/${id}`);
  },
  getEinlagernEinzelnListeartikel(id: number): Promise<AxiosResponse> {
    return apiClient.get(`/lager/einlagern/einzeln/listeartikel/${id}`);
  },
  //#endregion

  //#region LAGER MATERIAL
  getMaterialartikel(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/artikel`);
  },
  getAlleMaterialartikelFlaschen(): Promise<AxiosResponse> {
    return apiClient.get('/lager/material/artikel/flaschen');
  },
  getMateriallieferantartikel(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/material/lieferant/artikel`);
  },
  getLieferantartikel(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/lieferant/artikel`);
  },
  getLieferanten(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/lieferanten`);
  },
  getLieferherstartikel(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/lieferant/hersteller/artikel`);
  },
  getHerstellerartikel(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/hersteller/artikel`);
  },
  getHersteller(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/material/hersteller`);
  },
  //#endregion

  //#region VERWALTUNG/PRINTDATA
  getPrintdate(): Promise<AxiosResponse> {
    return apiClient.get(`/lager/verwaltung/printdata/files`);
  },
  downloadPrintdate(fileName: string): Promise<AxiosResponse> {
    return apiClient.get(`/lager/verwaltung/printdata/download/${fileName}`, {
      responseType: 'blob',
    });
  },
  uploadPrintdate(file: FormData): Promise<AxiosResponse> {
    return apiClient.post(`/lager/verwaltung/printdata/upload`, file, {
      onUploadProgress: (progressEvent) => {
        var progressBar = document.getElementById('uploadProgress');
        if (progressBar != null && progressBar.firstChild != null) {
          const child = progressBar.firstChild as HTMLElement;
          child.style.width = Math.round((progressEvent.loaded * 100) / progressEvent.total) + '%';
        }
      },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePrintdate(fileName: string): Promise<AxiosResponse> {
    return apiClient.delete(`/lager/verwaltung/printdata/delete/${fileName}`);
  },
  //#endregion

  //#region MDM
  syncMdmMaterialartikelEtiketten(): Promise<AxiosResponse> {
    return apiClient.get(`/mdm/sync/materialartikel/etiketten`);
  },

  syncMdmMaterialartikelKartons(): Promise<AxiosResponse> {
    return apiClient.get(`/mdm/sync/materialartikel/kartons`);
  },

  syncMdmMaterialartikelFlaschen(): Promise<AxiosResponse> {
    return apiClient.get(`/mdm/sync/materialartikel/flaschen`);
  },
  //#endregion

  //#endregion LAGER SERVICE

  //#region PPS SERVICE

  // retryRequest(config: AxiosRequestConfig) {
  //   return apiClient.request(config);
  // },

  // Produktion
  getMaschinen(): Promise<AxiosResponse> {
    return apiClient.get('pps/produktion/maschinen');
  },
  getProduktionsstatus(): Promise<AxiosResponse> {
    return apiClient.get('pps/produktion/status');
  },
  getProduktionspuffer(): Promise<AxiosResponse> {
    return apiClient.get('pps/produktion/lagerpuffer');
  },

  // Mitarbeiter
  getMitarbeiterPersonalstatus(): Promise<AxiosResponse> {
    return apiClient.get('pps/mitarbeiter/kostenstelle');
  },

  // Material
  getAlleFlaschenBestand(): Promise<AxiosResponse> {
    return apiClient.get('pps/material/flaschen/bestand');
  },
  getAlleKartonsBestand(): Promise<AxiosResponse> {
    return apiClient.get('pps/material/kartons/bestand');
  },
  getAlleKapselnBestand(): Promise<AxiosResponse> {
    return apiClient.get('pps/material/kapseln/bestand');
  },
  //#endregion

  //#region API SERVICE
  sendEmail(email: Email): Promise<AxiosResponse> {
    return apiClient.post(`/email/senden`, email);
  },

  //#endregion API SERVICE

  //#region Material
  getCurrentlyWorkingMitarbeiter(): Promise<AxiosResponse> {
    return apiClient.get('/hr/currently-working');
  },
  //#endregion
};
